import { useState, useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, CalendarDays } from "lucide-react";

/**
 * EddPicker - Grouped EDD selection with calendar mini-view and expandable time slots.
 * Props:
 *   eddGroups: [{ date: "2026-03-24", total: 22, times: [{ value: "2026-03-24T16:30:00.000+00:00", count: 17 }, ...] }]
 *   selectedEdds: string[] (full timestamp values)
 *   onEddChange: (timestamp, checked) => void
 */
const EddPicker = ({ eddGroups = [], selectedEdds = [], onEddChange }) => {
  const [expandedDates, setExpandedDates] = useState({});
  const [viewMode, setViewMode] = useState("list"); // "list" or "calendar"

  const toggleDate = (date) => {
    setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  // Check if ALL times for a date are selected
  const isDateFullySelected = (group) => {
    return group.times.every(t => selectedEdds.includes(t.value));
  };

  // Check if SOME times for a date are selected
  const isDatePartiallySelected = (group) => {
    const selected = group.times.filter(t => selectedEdds.includes(t.value));
    return selected.length > 0 && selected.length < group.times.length;
  };

  // Toggle all times for a date
  const toggleAllForDate = (group) => {
    const allSelected = isDateFullySelected(group);
    group.times.forEach(t => {
      if (allSelected) {
        if (selectedEdds.includes(t.value)) onEddChange(t.value, false);
      } else {
        if (!selectedEdds.includes(t.value)) onEddChange(t.value, true);
      }
    });
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const formatTime = (timestamp) => {
    const d = new Date(timestamp);
    return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
  };

  const getDayName = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-IN", { weekday: "short" });
  };

  // Calendar mini-view data
  const calendarWeeks = useMemo(() => {
    if (eddGroups.length === 0) return [];

    const dateSet = {};
    eddGroups.forEach(g => { dateSet[g.date] = g; });

    // Find range
    const dates = eddGroups.map(g => new Date(g.date + "T00:00:00"));
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    // Start from Monday of minDate's week
    const start = new Date(minDate);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));

    // End at Sunday of maxDate's week
    const end = new Date(maxDate);
    end.setDate(end.getDate() + (7 - end.getDay()) % 7);

    const weeks = [];
    let current = new Date(start);
    while (current <= end) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        const dateStr = current.toISOString().split("T")[0];
        const group = dateSet[dateStr];
        const isSelected = group ? group.times.some(t => selectedEdds.includes(t.value)) : false;
        const isFullySelected = group ? isDateFullySelected(group) : false;
        week.push({
          date: new Date(current),
          dateStr,
          day: current.getDate(),
          group,
          hasData: !!group,
          isSelected,
          isFullySelected,
        });
        current.setDate(current.getDate() + 1);
      }
      weeks.push(week);
    }
    return weeks;
  }, [eddGroups, selectedEdds]);

  const totalSelected = selectedEdds.length;

  return (
    <div className="space-y-2" data-testid="edd-picker">
      {/* Header with toggle */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5 text-emerald-600" />
          Expected Delivery
        </span>
        <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5">
          <button
            onClick={() => setViewMode("list")}
            className={`px-2 py-0.5 text-xs rounded transition-colors ${viewMode === "list" ? "bg-white shadow-sm text-slate-800 font-medium" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="edd-view-list"
          >
            List
          </button>
          <button
            onClick={() => setViewMode("calendar")}
            className={`px-2 py-0.5 text-xs rounded transition-colors ${viewMode === "calendar" ? "bg-white shadow-sm text-slate-800 font-medium" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="edd-view-calendar"
          >
            Cal
          </button>
        </div>
      </div>

      {totalSelected > 0 && (
        <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
          {totalSelected} slot{totalSelected > 1 ? "s" : ""} selected
        </Badge>
      )}

      {/* Calendar Mini View */}
      {viewMode === "calendar" && calendarWeeks.length > 0 && (
        <div className="border rounded-lg overflow-hidden bg-white">
          {/* Day headers */}
          <div className="grid grid-cols-7 bg-slate-50 border-b">
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map(d => (
              <div key={d} className="text-center text-xs font-medium text-slate-500 py-1">{d}</div>
            ))}
          </div>
          {/* Weeks */}
          {calendarWeeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7">
              {week.map((cell) => (
                <button
                  key={cell.dateStr}
                  onClick={() => {
                    if (cell.group) {
                      toggleAllForDate(cell.group);
                    }
                  }}
                  disabled={!cell.hasData}
                  className={`h-8 text-[10px] flex flex-col items-center justify-center border-b border-r transition-colors leading-tight
                    ${!cell.hasData ? "text-slate-300 cursor-default" : "cursor-pointer hover:bg-emerald-50"}
                    ${cell.isFullySelected ? "bg-emerald-100 text-emerald-800 font-bold" : ""}
                    ${cell.isSelected && !cell.isFullySelected ? "bg-emerald-50 text-emerald-700" : ""}
                  `}
                  title={cell.hasData ? `${formatDate(cell.dateStr)} (${cell.group.total})` : ""}
                >
                  <span className="font-medium">{cell.day}</span>
                  {cell.hasData && (
                    <span className={`text-[8px] ${cell.isSelected ? "text-emerald-600 font-semibold" : "text-slate-400"}`}>
                      {cell.group.total}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <div className="max-h-52 overflow-y-auto border rounded-lg bg-white">
          {eddGroups.map((group) => {
            const isExpanded = expandedDates[group.date];
            const fullySelected = isDateFullySelected(group);
            const partiallySelected = isDatePartiallySelected(group);
            const hasMultipleTimes = group.times.length > 1;

            return (
              <div key={group.date} className={`${fullySelected ? "bg-emerald-50" : partiallySelected ? "bg-blue-50/50" : ""}`}>
                {/* Date Header */}
                <div
                  className="flex items-center gap-1.5 px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
                  onClick={() => hasMultipleTimes ? toggleDate(group.date) : toggleAllForDate(group)}
                >
                  {hasMultipleTimes && (
                    isExpanded
                      ? <ChevronDown className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      : <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  )}
                  <Checkbox
                    checked={fullySelected}
                    className={`h-3.5 w-3.5 ${partiallySelected && !fullySelected ? "data-[state=unchecked]:bg-blue-100 data-[state=unchecked]:border-blue-300" : ""}`}
                    onCheckedChange={() => toggleAllForDate(group)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="text-sm font-medium text-slate-800 whitespace-nowrap">{formatDate(group.date)}</span>
                  <span className="text-xs text-slate-400 whitespace-nowrap">{getDayName(group.date)}</span>
                  <span className="text-xs text-slate-500 ml-auto whitespace-nowrap font-medium">({group.total})</span>
                </div>

                {/* Time Slots (expanded) */}
                {isExpanded && hasMultipleTimes && (
                  <div className="pl-8 pb-1 border-b border-slate-100">
                    {group.times.map((t) => {
                      const isChecked = selectedEdds.includes(t.value);
                      return (
                        <div key={t.value}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded cursor-pointer transition-colors
                            ${isChecked ? "bg-emerald-100/60" : "hover:bg-slate-50"}`}
                          onClick={() => onEddChange(t.value, !isChecked)}
                        >
                          <Checkbox checked={isChecked} className="h-3 w-3"
                            onCheckedChange={(checked) => onEddChange(t.value, checked)}
                            onClick={(e) => e.stopPropagation()} />
                          <span className="text-xs text-slate-600">{formatTime(t.value)}</span>
                          <span className="text-xs text-slate-400">({t.count})</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Single time slot — show time inline on header */}
                {!hasMultipleTimes && group.times.length === 1 && null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EddPicker;
